// Import Actions
import Immutable from "seamless-immutable";
import { isString } from "lodash";
import {
  APP_ERROR,
  REHYDRATION_COMPLETE,
  ROUTE_CHANGE,
  ON_BACK_BUTTON_PRESS,
  RESET_ROUTE_CHANGE_STATE,
  UPDATE_APP_LOCATION,
  CLEAR_APP_ERRORS,
  CLOSE_MODAL,
  CLOSE_ONE_OFF_MODAL,
} from "../types/App";

// Initial State
const INITIAL_STATE = Immutable({
  errors: {},
  rehydrationComplete: false,
  location: {
    previous: null,
    current: null,
    query: null,
  },
  routeChanged: false,
  modalWasClosed: false,
  oneOffModalClosed: {},
});

const constructErrors = (errors) => {
  if (errors.fieldName) {
    return {
      [errors.fieldName]: errors,
    };
  }

  return {
    app: errors,
  };
};

const removeFieldError = (errors, fieldName = "app") => {
  if (Immutable.getIn(errors, [fieldName])) {
    return Immutable.without(errors, fieldName);
  }

  return errors;
};

const updateLocation = (previous, current) => {
  return {
    previous,
    current,
  };
};

const getUpdatedQuery = (newQuery, oldQuery = "") => {
  return isString(newQuery) ? newQuery : oldQuery;
};

const AppReducer = (state = INITIAL_STATE, action) => {
  const immutableState = Immutable.isImmutable(state)
    ? state
    : Immutable(state);

  switch (action.type) {
    case CLOSE_ONE_OFF_MODAL:
      return Immutable.updateIn(state, ["oneOffModalClosed"], () => ({
        [action.modalKey]: new Date(),
      }));
    case CLOSE_MODAL:
      return Immutable.updateIn(state, ["modalWasClosed"], () => true);
    case UPDATE_APP_LOCATION:
      const { location } = immutableState;

      return immutableState.merge({
        location: {
          // If new pathname received, then make state.location.current
          // the previous path viewed
          previous: action.pathname ? location.current : location.previous,
          // If new pathname received, then make that the new
          // current path viewed
          current: action.pathname || location.current,
          // If a new query is passed in, then update it.
          query: getUpdatedQuery(action.query, location.query),
        },
      });
    case RESET_ROUTE_CHANGE_STATE:
      return immutableState.merge({
        routeChanged: false,
      });
    case ON_BACK_BUTTON_PRESS:
    case ROUTE_CHANGE:
      // Set the updateState default 'location' value to be
      // either state.location if it exists OR fallback to INITIAL_STATE.location
      // as we always need a location value set.
      const updateState = {
        errors: removeFieldError(state.errors, action.fieldName),
        routeChanged: true,
        location: state.location || INITIAL_STATE.location,
      };

      // Only update location history if it's not
      // the 404 page and action.pathname is passed in
      if (action.pathname && action.pathname !== "/not-found") {
        updateState.location = updateLocation(
          updateState.location.current,
          action.pathname
        );
      }

      // If query is passed in, update it in location.query
      if (isString(action.query)) {
        updateState.location.query = getUpdatedQuery(action.query);
      }

      return immutableState.merge(updateState);
    case APP_ERROR:
      return immutableState.merge({
        errors: constructErrors(action.errors),
      });
    case CLEAR_APP_ERRORS:
      return immutableState.merge({
        errors: {},
      });
    case REHYDRATION_COMPLETE:
      const { pathname, search } = action.history.location;

      return immutableState.merge({
        rehydrationComplete: true,
        errors: {},
        location: {
          previous: null,
          current: pathname,
          query: getUpdatedQuery(search),
        },
        routeChanged: false,
      });
    default:
      return immutableState;
  }
};

// Export Reducer
export default AppReducer;
